import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import moment from "moment";
import toast from "react-hot-toast";
import { useGetFilters } from "../hooks/useGetFilters";
import { useGetRows } from "../hooks/useGetRows";
import { useGetMontosLiquidacion } from "../hooks/useGetMontosLiquidacion";
import { useAjusteLiquidacion } from "../hooks/useAjusteLiquidacion";
import { useSeleccionLiquidaciones } from "../hooks/useSeleccionLiquidaciones";
import { DialogDesestimarLiquidacion } from "./Dialogs/DialogDesestimarLiquidacion";
import { DialogDetalleLiquidacion } from "./Dialogs/DialogDetalleLiquidacion";
import { DialogSolicitarAjusteLiquidacion } from "./Dialogs/DialogSolicitarAjusteLiquidacion";
import { DialogConfirmacion } from "./Dialogs/DialogConfirmacionLiquidar";
import { PreLiquidacionFilters } from "./PreLiquidacionFilters";
import { PreLiquidacionHeader } from "./PreLiquidacionHeader";
import { PreLiquidacionGrillaWrapper } from "./PreLiquidacionGrillaWrapper";
import { useLocation } from "react-router-dom";
import { useProgressiveList } from "../../../hooks/useProgressiveList";
import { useGetPorcentajeMaxMontoAjuste } from "../hooks/useGetPorcentajeMaxMontoAjuste";
import { useDebounce } from "../../../hooks/useDebounce";
import { filtrarLiquidacionesPorTexto } from "../utils/filterLiquidacionesPorTexto";

export const ID_ESTADO_LIQUIDACION_APROBADO = 2;
export const ID_ESTADO_LIQUIDACION_RECHAZO = 3;

export const PreLiquidacionContent = ({ setChildrenAction }) => {
    const rutasStorage = localStorage.getItem('navigationRoutes');
    const location = useLocation();
    const scrollContainerRef = useRef(null);

    const [scrollHeight, setScrollHeight] = useState('auto');
    const [allowedWrite, setAllowedWrite] = useState(false);
    const [openDetalle, setOpenDetalle] = useState(false);
    const [openAjuste, setOpenAjuste] = useState(false);
    const [openDesestimar, setOpenDesestimar] = useState(false);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [openConfirmAprobar, setOpenConfirmAprobar] = useState(false);
    const [liquidacionSeleccionada, setLiquidacionSeleccionada] = useState(null);
    const [isRefetchRequired, setIsRefetchRequired] = useState(true);

    const {
        filters,
        handleChangeFilters,
        delegaciones,
        profesionales,
        estados,
        tiposProfesional,
    } = useGetFilters(setIsRefetchRequired, false);

    const {
        handleSearch: searchLiquidaciones,
        liquidacionesPorProfesional,
        loading: loadingRows,
        busquedaCompletada
    } = useGetRows(filters, setIsRefetchRequired);

    const {
        montos,
        fetchMontos,
        loading: loadingMontos
    } = useGetMontosLiquidacion();

    const { porcentajeMaxMontoAjuste, valorMaxMontoAjusteCP } = useGetPorcentajeMaxMontoAjuste();

    const refrescarTodo = () => {
        searchLiquidaciones();
        fetchMontos(filters);
    };

    const {
        motivos,
        ajusteExistente,
        fetchAjuste,
        limpiarAjuste,
        guardarAjuste,
        eliminarAjuste,
        loadingMutation
    } = useAjusteLiquidacion({ onSuccess: refrescarTodo });

    const {
        getSeleccionProfesional,
        toggleSeleccion,
        seleccionarTodos,
        aprobar,
        desestimar,
        loading: loadingMasivo,
        cantidadSeleccionada
    } = useSeleccionLiquidaciones({ onSuccess: refrescarTodo });

    const handleViewDetalle = (row) => {
        setLiquidacionSeleccionada(row);
        setOpenDetalle(true);
    };

    const handleEditAjuste = (row) => {
        setLiquidacionSeleccionada(row);
        setOpenAjuste(true);
    };

    const handleDeleteAjuste = (row) => {
        setLiquidacionSeleccionada(row);
        setOpenConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        setOpenConfirmDelete(false);
        if (!liquidacionSeleccionada) return;
        await eliminarAjuste(liquidacionSeleccionada.idLiquidacionProfesionalDetalle);
    };

    const handleClickAprobar = () => {
        if (cantidadSeleccionada === 0) return;
        setOpenConfirmAprobar(true);
    };

    const getProfesionalesConLiquidacionCero = () => {
        const profesionales = [];

        liquidacionesPorProfesional.forEach(({ idProfesional, profesionalData, liquidaciones }) => {
            const seleccion = getSeleccionProfesional(idProfesional);
            if (!seleccion || seleccion.size === 0) return;

            const evaluacionesCero = liquidaciones
                .filter(
                    (l) =>
                        seleccion.has(l.idLiquidacionProfesionalDetalle) &&
                        !((l.liquidacionTotal ?? 0) > 0)
                )
                .map((l) => l.idEvaluacion)
                .filter((idEvaluacion, index, arr) => arr.indexOf(idEvaluacion) === index);

            if (evaluacionesCero.length > 0) {
                profesionales.push({
                    nombre: profesionalData.profesional,
                    evaluaciones: evaluacionesCero
                });
            }
        });

        return profesionales;
    };

    const handleConfirmAprobar = async () => {
        const profesionalesConCero = getProfesionalesConLiquidacionCero();
        if (profesionalesConCero.length > 0) {
            const detalle = profesionalesConCero
                .map((p) => `${p.nombre} (Contrato/s: ${p.evaluaciones.join(', ')})`)
                .join('; ');
            const mensaje = profesionalesConCero.length === 1
                ? `El Profesional ${detalle} tiene liquidaciones con valor 0`
                : `Los Profesionales ${detalle} tienen liquidaciones con valor 0`;
            toast.error(mensaje);
            setOpenConfirmAprobar(false);
            return;
        }

        const ok = await aprobar();
        if (ok) setOpenConfirmAprobar(false);
    };

    const handleClickDesestimar = () => {
        if (cantidadSeleccionada === 0) return;
        setOpenDesestimar(true);
    };

    const handleSearch = () => {
        refrescarTodo();
    };

    useEffect(() => {
        setChildrenAction?.(
            <PreLiquidacionHeader
                mes={filters.mes}
                totalMesAnterior={!isRefetchRequired ? montos.totalMesAnterior : null}
                totalMesAnteAnterior={!isRefetchRequired ? montos.totalMesAnteAnterior : null}
                allowedWrite={allowedWrite}
                cantidadSeleccionada={cantidadSeleccionada}
                handleClickDesestimar={handleClickDesestimar}
                handleClickAprobar={handleClickAprobar}
            />
        );
    }, [
        filters.mes,
        montos.totalMesAnterior,
        montos.totalMesAnteAnterior,
        cantidadSeleccionada,
        setChildrenAction,
        allowedWrite,
        isRefetchRequired
    ]);

    useEffect(() => {
        //Permisos para escritura
        if (rutasStorage) {
            // Por ruta (el usuario debe tener permisos de escritura de la ruta)
            const rutas = JSON.parse(rutasStorage);
            const rutaQueSirve = location.pathname;
            const escrituraRuta = rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
            )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura;

            //Por estado (la liquidación no puede estar aprobada)
            const noLiqAprobada = filters.idEstado !== ID_ESTADO_LIQUIDACION_APROBADO;

            //Por mes (solo se permite escribir en el mes anterior al actual, o en meses anteriores)
            const prevMonth = moment().subtract(1, 'month');
            const isPreviousMonth =
                filters.mes === prevMonth.month() + 1 &&
                filters.anio === prevMonth.year();

            setAllowedWrite(escrituraRuta && noLiqAprobada && isPreviousMonth);
        }
    }, [rutasStorage, filters.idEstado, filters.mes, filters.anio, location.pathname]);

    const cargando = useMemo(() => (loadingRows || loadingMontos), [loadingRows, loadingMontos]);

    const debouncedFiltrar = useDebounce(filters.filtrar, 1000);

    const liquidacionesFiltradas = useMemo(() => {
        return filtrarLiquidacionesPorTexto(liquidacionesPorProfesional, debouncedFiltrar);
    }, [debouncedFiltrar, liquidacionesPorProfesional]);

    const sinResultados = useMemo(() => {
        return (busquedaCompletada && !cargando && liquidacionesFiltradas.length === 0)
    }, [
        busquedaCompletada,
        cargando,
        liquidacionesFiltradas.length
    ]);

    useEffect(() => {
        const calcHeight = () => {
            if (scrollContainerRef.current) {
                const offsetTop = scrollContainerRef.current.getBoundingClientRect().top;
                const paddingMainContent = 60;
                setScrollHeight(`calc(100vh - ${offsetTop}px - ${paddingMainContent}px)`);
            }
        };

        calcHeight();
        window.addEventListener('resize', calcHeight);
        return () => window.removeEventListener('resize', calcHeight);
    }, []);

    const visibleLiquidaciones = useProgressiveList(liquidacionesFiltradas, 5);

    return (
        <div>
            <PreLiquidacionFilters
                filters={filters}
                handleChangeFilters={handleChangeFilters}
                handleSearch={handleSearch}
                delegacionesOptions={delegaciones}
                profesionalesOptions={profesionales}
                estadosOptions={estados}
                tiposProfesionalOptions={tiposProfesional}
            />

            <Box
                ref={scrollContainerRef}
                sx={{ height: scrollHeight, overflowY: 'auto', pr: 1, scrollbarColor: '#8b8b8b transparent', minHeight: 230 }}
            >
                {isRefetchRequired && !cargando && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body1' color='text.secondary'>
                            Haz click en "Buscar" para aplicar los nuevos filtros
                        </Typography>
                    </Box>
                )}

                {cargando && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={40} />
                    </Box>
                )}

                {!isRefetchRequired && sinResultados && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body1' color='text.secondary'>
                            No se encontraron liquidaciones para los filtros seleccionados.
                        </Typography>
                    </Box>
                )}

                {!cargando && !isRefetchRequired && visibleLiquidaciones.map(
                    ({ idProfesional, profesionalData, liquidaciones, idEstadoLiquidacionProfesional }, index) => (
                        <PreLiquidacionGrillaWrapper
                            key={idProfesional}
                            idProfesional={idProfesional}
                            profesionalData={profesionalData}
                            liquidaciones={liquidaciones}
                            idEstadoLiquidacionProfesional={idEstadoLiquidacionProfesional}
                            mesLiquidado={filters.mes}
                            allowedWrite={allowedWrite}
                            seleccionadosProfesional={getSeleccionProfesional(idProfesional)}
                            toggleSeleccion={toggleSeleccion}
                            seleccionarTodos={seleccionarTodos}
                            handleEditAjuste={handleEditAjuste}
                            handleViewDetalle={handleViewDetalle}
                            handleDeleteAjuste={handleDeleteAjuste}
                            showDivider={(index + 1) !== liquidacionesFiltradas.length}
                        />
                    ))}
            </Box>

            <DialogDetalleLiquidacion
                open={openDetalle}
                setOpen={setOpenDetalle}
                liquidacion={liquidacionSeleccionada}
                mes={filters.mes}
                anio={filters.anio}
                idTipoProfesional={filters.idTipoProfesional}
            />

            <DialogSolicitarAjusteLiquidacion
                open={openAjuste}
                setOpen={setOpenAjuste}
                liquidacion={liquidacionSeleccionada}
                motivos={motivos}
                ajusteExistente={ajusteExistente}
                fetchAjuste={fetchAjuste}
                limpiarAjuste={limpiarAjuste}
                guardarAjuste={guardarAjuste}
                loadingMutation={loadingMutation}
                mes={filters.mes}
                anio={filters.anio}
                porcentajeMaxMontoAjuste={porcentajeMaxMontoAjuste}
                valorMaxMontoAjusteCP={valorMaxMontoAjusteCP}
                idTipoProfesional={filters.idTipoProfesional}
            />

            <DialogDesestimarLiquidacion
                open={openDesestimar}
                setOpen={setOpenDesestimar}
                cantidadSeleccionada={cantidadSeleccionada}
                onConfirmar={desestimar}
                loading={loadingMasivo}
            />

            <DialogConfirmacion
                open={openConfirmDelete}
                title={`¿Está seguro que desea eliminar el ajuste del contrato ${liquidacionSeleccionada?.idEvaluacion ?? ''}?`}
                onCancel={() => setOpenConfirmDelete(false)}
                onAccept={handleConfirmDelete}
            />

            <DialogConfirmacion
                open={openConfirmAprobar}
                title={`¿Está seguro que desea aprobar ${cantidadSeleccionada} liquidación(es)?`}
                onCancel={() => setOpenConfirmAprobar(false)}
                onAccept={handleConfirmAprobar}
                loading={loadingMasivo}
            />
        </div>
    );
};